import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Search, Car, Plus, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

export default function CustomerNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "", customer_type: "individual", company_name: "", vat_registered: false, vat_number: "" });
  const [saving, setSaving] = useState(false);

  // Optional vehicle
  const [addVehicle, setAddVehicle] = useState(false);
  const [vrm, setVrm] = useState("");
  const [looking, setLooking] = useState(false);
  const [veh, setVeh] = useState({ make: "", model: "", year: "", colour: "", fuel_type: "", engine_capacity: "", mot_expiry: "", tax_expiry: "" });
  const [looked, setLooked] = useState(false);

  const lookup = async () => {
    if (!vrm.trim()) return;
    setLooking(true);
    try {
      const d = await api(`/dvla/lookup/${encodeURIComponent(vrm.trim())}`);
      setVeh({ make: d.make || "", model: d.model || "", year: d.year || "", colour: d.colour || "", fuel_type: d.fuel_type || "", engine_capacity: d.engine_capacity || "", mot_expiry: d.mot_expiry || "", tax_expiry: d.tax_expiry || "" });
      setLooked(true);
      toast.success("Vehicle found on DVLA");
    } catch (e) { toast.error(e.message || "Vehicle not found"); } finally { setLooking(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (addVehicle && (!vrm.trim() || !veh.make || !veh.model)) { toast.error("Vehicle needs VRM, make and model (or turn off Add Vehicle)"); return; }
    setSaving(true);
    try {
      const c = await api("/customers", { method: "POST", body: JSON.stringify({
        name: form.name.trim(), phone: form.phone.trim() || null, email: form.email.trim() || null,
        address: form.address.trim() || null, notes: form.notes.trim() || null,
        customer_type: form.customer_type,
        company_name: form.customer_type === "company" ? (form.company_name.trim() || null) : null,
        vat_registered: !!form.vat_registered,
        vat_number: form.vat_registered ? (form.vat_number.trim() || null) : null,
      }) });
      if (addVehicle && vrm.trim() && veh.make && veh.model) {
        await api("/vehicles", { method: "POST", body: JSON.stringify({
          vrm: vrm.trim(), make: veh.make, model: veh.model,
          year: veh.year ? Number(veh.year) : null, colour: veh.colour || null, fuel_type: veh.fuel_type || null,
          engine_capacity: veh.engine_capacity ? Number(veh.engine_capacity) : null,
          mot_expiry: veh.mot_expiry || null, tax_expiry: veh.tax_expiry || null, customer_id: c.id,
        }) });
        toast.success("Customer and vehicle created");
      } else {
        toast.success("Customer created");
      }
      navigate(`/customers/${c.id}`);
    } catch (e2) { toast.error(e2.message || "Could not create customer"); } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-xl">
      <Link to="/customers" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Customers</Link>
      <PageHeader title="New Customer" />
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-4 rounded-xl border border-border bg-card p-6">
          <F label="NAME *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="cust-name" placeholder="Alan Turner" />
          <F label="PHONE" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="cust-phone" placeholder="07700 900123" />
          <F label="EMAIL" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="cust-email" type="email" placeholder="alan@example.co.uk" />
          <F label="ADDRESS" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="cust-address" placeholder="12 High Street, London" />
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">NOTES</span>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cust-notes" rows={2} className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>

          <div>
            <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">CUSTOMER TYPE</span>
            <div className="flex gap-2">
              {["individual", "company"].map((t) => (
                <button key={t} type="button" onClick={() => setForm({ ...form, customer_type: t })} data-testid={`cust-type-${t}`}
                  className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-bold capitalize ${form.customer_type === t ? "border-brand bg-brand-tertiary text-brand" : "border-border bg-card text-midnight"}`}>{t}</button>
              ))}
            </div>
          </div>
          {form.customer_type === "company" && (
            <F label="COMPANY NAME" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testId="cust-company-name" placeholder="Acme Logistics Ltd" />
          )}
          <label className="flex cursor-pointer items-center gap-3">
            <input type="checkbox" checked={form.vat_registered} onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })} data-testid="cust-vat-registered" className="h-5 w-5 rounded border-input" />
            <span className="text-sm font-bold text-midnight">VAT registered</span>
          </label>
          {form.vat_registered && (
            <F label="VAT NUMBER" value={form.vat_number} onChange={(v) => setForm({ ...form, vat_number: v })} testId="cust-vat-number" placeholder="GB123456789" />
          )}
        </div>

        {/* Optional vehicle */}
        <div className="rounded-xl border border-border bg-card p-6">
          <label className="flex cursor-pointer items-center gap-3">
            <input type="checkbox" checked={addVehicle} onChange={(e) => setAddVehicle(e.target.checked)} data-testid="add-vehicle-toggle" className="h-5 w-5 rounded border-input" />
            <span className="flex items-center gap-2 text-sm font-bold text-midnight"><Car className="h-4 w-4 text-brand" />Add a vehicle for this customer (optional)</span>
          </label>

          {addVehicle && (
            <div className="mt-4 space-y-3">
              <div className="flex gap-2">
                <input value={vrm} onChange={(e) => setVrm(e.target.value.toUpperCase())} placeholder="AB12 XYZ" data-testid="cust-veh-vrm"
                  className="flex-1 rounded-lg border border-input bg-plate px-3.5 py-2.5 text-center font-mono text-lg font-bold uppercase tracking-wider text-black outline-none focus:ring-2 focus:ring-brand/40" />
                <button type="button" onClick={lookup} disabled={looking} data-testid="cust-veh-lookup" className="inline-flex items-center gap-2 rounded-lg bg-midnight px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                  {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Lookup
                </button>
              </div>
              {looked && <p className="flex items-center gap-1 text-xs font-semibold text-success"><CheckCircle2 className="h-3.5 w-3.5" />DVLA details loaded</p>}
              <div className="grid grid-cols-2 gap-2">
                <VF label="MAKE *" v={veh.make} on={(x) => setVeh({ ...veh, make: x })} t="cust-veh-make" />
                <VF label="MODEL *" v={veh.model} on={(x) => setVeh({ ...veh, model: x })} t="cust-veh-model" />
                <VF label="YEAR" v={veh.year} on={(x) => setVeh({ ...veh, year: x })} t="cust-veh-year" />
                <VF label="COLOUR" v={veh.colour} on={(x) => setVeh({ ...veh, colour: x })} t="cust-veh-colour" />
              </div>
            </div>
          )}
        </div>

        <button type="submit" disabled={saving} data-testid="cust-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Plus className="h-4 w-4" />{addVehicle ? "SAVE CUSTOMER & VEHICLE" : "SAVE CUSTOMER"}</>}
        </button>
      </form>
    </div>
  );
}

function F({ label, value, onChange, testId, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">{label}</span>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-lg border border-input bg-card px-3.5 py-3 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
    </label>
  );
}
function VF({ label, v, on, t }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">{label}</span>
      <input data-testid={t} value={v} onChange={(e) => on(e.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-brand" />
    </label>
  );
}
